import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getProductUrl} from '@amblique/king-living/app/components/want-it-sooner';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {getFinishedGoodCoverVariantId} from '@amblique/king-living/app/utils/product-utils';
import {isFinishedGoodSimple} from '@amblique/king-living/app/utils/inventory-utils';

import {FinishedGoodCoverRow} from './cover-row';
import {FinishedGoodNotPerfectMatchCard} from './not-perfect-match-card';
import {FinishedGoodOptionsList} from './options-list';
import {FinishedGoodSimpleDescription} from './simple-description';

/**
 * Finished Goods PDP body (TSD - PDP Simple §7 / KINDW-1123).
 *
 * Three stacked sections render between the layout trigger and the price/CTA strip:
 *
 *   1. <FinishedGoodCoverRow> — single static row for the bundle's cover variant.
 *   2. Options: full → <FinishedGoodOptionsList>; simple FG → longDescription.
 *   3. <FinishedGoodNotPerfectMatchCard> — Contentful CTA linking to the master PDP.
 *
 * Master product is fetched once in `<ProductView>` (cover variant → master id) and
 * passed in via `masterProduct` for the Customize link and options list.
 */
export function FinishedGoodView({product, masterProduct = null}) {
  const styles = useMultiStyleConfig('FinishedGoodView', {});

  const coverVariantId = getFinishedGoodCoverVariantId(product);
  const customizePdpHref = masterProduct ? getProductUrl(masterProduct) : null;
  const useSimpleDescription = isFinishedGoodSimple(product);

  const contentfulData = useProductDetailContentful();
  const customizeContent = contentfulData?.customizeContent ?? null;
  const customizeImageUrl = contentfulData?.customizeImageBackground?.url ?? null;

  return (
    <Box className="finished-good-view" data-product-kind="finished-good" {...styles.root}>
      <Text as="h2" className="finished-good-view__heading" {...styles.heading}>
        <FormattedMessage
          defaultMessage="What's included"
          id="finished_good.whats_included.heading"
        />
      </Text>
      <FinishedGoodCoverRow coverVariantId={coverVariantId} />
      {useSimpleDescription ? (
        <FinishedGoodSimpleDescription product={product} />
      ) : (
        <FinishedGoodOptionsList masterProduct={masterProduct} product={product} />
      )}
      <FinishedGoodNotPerfectMatchCard
        customizeContent={customizeContent}
        customizeImageUrl={customizeImageUrl}
        customizePdpHref={customizePdpHref}
      />
    </Box>
  );
}

FinishedGoodView.displayName = 'FinishedGoodView';

FinishedGoodView.propTypes = {
  masterProduct: PropTypes.object,
  product: PropTypes.object.isRequired,
};
